import { desanitizeKey } from '@/helper/utils';
import { AppDispatch } from '@/store';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllRolesDropdown } from '../../../store/slices/rolesPermissionsSlice/rolesPermissionsSlice';

interface Role {
  id: number;
  name: string;
  description: string;
  color: string;
}

interface RoleAssignmentProps {
  selectedRole: number;
  setSelectedRole: React.Dispatch<React.SetStateAction<number | null>>;
  welcomeMessage: string;
  setWelcomeMessage: React.Dispatch<React.SetStateAction<string>>;
  welcomeMessageError?: string;
  isEditMode: boolean;
}

const RoleAssignment: React.FC<RoleAssignmentProps> = ({
  selectedRole,
  setSelectedRole,
  welcomeMessage,
  setWelcomeMessage,
  welcomeMessageError,
  isEditMode,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const roles = useSelector((state: any) => state.rolesPermissions.filteredRoles);
  const [open, setOpen] = useState(false);
  const handleRoleChange = (roleValue: number) => {
    setSelectedRole(roleValue);
  };

  useEffect(() => {
    dispatch(getAllRolesDropdown());
  }, [dispatch]);

  const dropdownRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddRole = (roleId: number) => () => {
    setSelectedRole(roleId);
  };

  const roleColors = [
    'bg-[#CC167CCC] text-[#4A082D]',
    'bg-[#4CBEE7CC] text-[#4A082D]',
    'bg-[#E86A9BCC] text-[#4A082D]',
    'bg-[#4C86E7CC] text-[#4A082D]',
    'bg-[#5CC9A7CC] text-[#4A082D]',
  ];

  const getRoleColor = (index: number) => {
    return roleColors[index % roleColors.length];
  };

  return (
    <div className="rounded-md text-[#621C43]">
      <div className="border-b border-b-[#621C43] px-6 py-5">
        <h2 className="font-Lato text-[28px] font-bold mb-">Role Assignment</h2>
        <p className="font-Lato text-[20px] font-Medium">
          {!isEditMode
            ? 'Enter the basic details for the new admin user'
            : 'Update the role for the admin user'}
        </p>
      </div>

      <div className="p-6">
        <h1 className="font-Lato font-medium mb-1">
          Admin 1 <span className="text-red-500">*</span>
        </h1>

        {/* Role Dropdown */}
        <div ref={dropdownRef} className="relative w-full mb-4">
          <button
            onClick={() => setOpen(!open)}
            className="w-full p-2 italic font-medium rounded-md bg-[#4A082D33] text-[#621C43] flex justify-between items-center"
          >
            <span>
              {selectedRole
                ? desanitizeKey(roles.find((r: Role) => r.id === selectedRole)?.name || '')
                : 'Choose Role'}
            </span>

            <span className="ml-2">
              {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          </button>

          {/* Inline dropdown so it pushes content down */}
          {open && (
            <ul className="w-full p-2 bg-[#4A082D33] bg-opacity-20 overflow-auto space-y-1 rounded-md mt-1">
              {roles
                .filter((role: Role) => {
                  const roleInfo = JSON.parse(localStorage.getItem('roleInfo') || '{}');
                  const isAdmin = roleInfo.name === 'ADMIN';
                  return !(isAdmin && role.name === 'SUPER_ADMIN');
                })
                .map((role: Role, index: number) => (
                  <li
                    key={role.id}
                    onClick={() => {
                      setSelectedRole(role.id);
                      handleAddRole(role.id);
                      setOpen(false);
                    }}
                    className={`px-3 py-1 text-sm font-medium cursor-pointer text-[#621C43] rounded-md hover:bg-[#621C43] hover:text-white ${getRoleColor(
                      index,
                    )}`}
                  >
                    {desanitizeKey(role.name)} — {role.description}
                  </li>
                ))}
            </ul>
          )}
        </div>

        {/* Welcome Message */}
        <div className="">
          <label className="block font-medium mb-2">
            Welcome Message (Sent with invite) <span className="text-red-500">*</span>
          </label>
          <textarea
            value={welcomeMessage}
            onChange={(e) => setWelcomeMessage(e.target.value)}
            placeholder="Add a welcome message"
            className={`w-full p-3 text-[#4A082D] bg-[#4A082D33] rounded-md focus:ring-2 focus:ring-[#621C43] focus:outline-none ${
              welcomeMessageError ? 'border border-red-500' : ''
            }`}
            rows={8}
          />
          {welcomeMessageError && (
            <p className="text-red-500 text-sm mt-1">{welcomeMessageError}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleAssignment;
